/**
 * CSV parsing for ATTENDANCE_IMPORT, ported from the legacy importers. The browser parses and reports
 * row errors before anything is sent; the workflow checks every row again and decides what is saved.
 *
 * - `doorLog` (legacy UpdateAttendanceTime): a door/device punch log, one row per punch, header row
 *   first; column 1 the date, column 2 the time, column 6 the employee ID. Every row is for the same
 *   day; an employee's earliest punch is the clock-in and the latest the clock-out.
 * - `deviceExport` (legacy Kosovo upload): header `Employee Id, Attendance Date, Clock Start Time,
 *   Clock End Time, Break start Time, Break end Time`; one row per employee and day, any number of days.
 *
 * Times are wall times on the organization's clock ("HH:mm"); the workflow turns them into instants.
 */

export type ImportFormat = 'doorLog' | 'deviceExport';

export interface ImportRow {
  /** The file's line number (the header is line 1). */
  row: number;
  employeeId: string;
  /** "YYYY-MM-DD" */
  day: string;
  /** "HH:mm" */
  start: string;
  end: string;
  breakStart?: string;
  breakEnd?: string;
}

export interface ImportRowError {
  row: number;
  /** Translation key. */
  messageKey: string;
}

export interface ImportParseResult {
  rows: ImportRow[];
  errors: ImportRowError[];
  /** A problem with the whole file (translation key), when nothing can be imported. */
  fileError?: string;
}

export const DEVICE_EXPORT_HEADERS = [
  'Employee Id',
  'Attendance Date',
  'Clock Start Time',
  'Clock End Time',
  'Break start Time',
  'Break end Time',
];

const pad2 = (value: number) => String(value).padStart(2, '0');

/** The employee IDs ATTENDANCE_IMPORT accepts (its ValidateInput refuses a whole call otherwise). */
export const IMPORT_EMPLOYEE_ID_PATTERN = /^[A-Za-z0-9._/-]{1,64}$/;

const isRealDay = (year: number, month: number, day: number) => {
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
};

/**
 * "YYYY-MM-DD", "YYYY/MM/DD" or the day-first "D/M/YYYY" the door devices write (and the legacy server
 * read) → "YYYY-MM-DD", or null.
 */
export const parseImportDay = (text: string): string | null => {
  const value = text.trim();
  let match = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(value);
  let year: number;
  let month: number;
  let day: number;
  if (match) {
    [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  } else {
    match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (!match) return null;
    [day, month, year] = [Number(match[1]), Number(match[2]), Number(match[3])];
  }
  return isRealDay(year, month, day) ? `${year}-${pad2(month)}-${pad2(day)}` : null;
};

/** "H:mm", "HH:mm:ss" or "h:mm[:ss] AM/PM" → "HH:mm" (seconds dropped), or null. */
export const parseImportTime = (text: string): string | null => {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?$/.exec(text.trim());
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2]);
  const meridiem = match[4]?.toUpperCase();
  if (minute > 59 || (match[3] && Number(match[3]) > 59)) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem === 'PM' ? 12 : 0);
  }
  if (hour > 23) return null;
  return `${pad2(hour)}:${pad2(minute)}`;
};

/** The file's lines as cells (comma-separated, surrounding quotes removed), with their line numbers. */
const linesOf = (text: string) =>
  text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line, index) => ({
      row: index + 1,
      cells: line.split(',').map((cell) =>
        cell
          .trim()
          .replace(/^"(.*)"$/, '$1')
          .trim()
      ),
      blank: line.trim() === '',
    }));

const parseDoorLog = (text: string): ImportParseResult => {
  const errors: ImportRowError[] = [];
  const punches: { row: number; employeeId: string; day: string; time: string }[] = [];
  for (const line of linesOf(text).slice(1)) {
    if (line.blank) continue;
    const day = parseImportDay(line.cells[0] ?? '');
    const time = parseImportTime(line.cells[1] ?? '');
    const employeeId = line.cells[5] ?? '';
    if (line.cells.length < 6 || !employeeId)
      errors.push({ row: line.row, messageKey: 'IMPORT_ROW_COLUMNS_MISSING' });
    else if (!IMPORT_EMPLOYEE_ID_PATTERN.test(employeeId))
      errors.push({ row: line.row, messageKey: 'IMPORT_ROW_EMPLOYEE_INVALID' });
    else if (!day) errors.push({ row: line.row, messageKey: 'IMPORT_ROW_DATE_INVALID' });
    else if (!time) errors.push({ row: line.row, messageKey: 'IMPORT_ROW_TIME_INVALID' });
    else punches.push({ row: line.row, employeeId, day, time });
  }
  if (new Set(punches.map((p) => p.day)).size > 1)
    return { rows: [], errors, fileError: 'IMPORT_ALL_DATES_MUST_MATCH' };

  const byEmployee = new Map<string, typeof punches>();
  for (const punch of punches)
    byEmployee.set(punch.employeeId, [...(byEmployee.get(punch.employeeId) ?? []), punch]);
  const rows: ImportRow[] = [];
  for (const [employeeId, list] of byEmployee) {
    const times = list.map((p) => p.time).sort();
    if (times[0] === times[times.length - 1]) {
      errors.push({ row: list[0].row, messageKey: 'IMPORT_ROW_SINGLE_PUNCH' });
      continue;
    }
    rows.push({
      row: list[0].row,
      employeeId,
      day: list[0].day,
      start: times[0],
      end: times[times.length - 1],
    });
  }
  return { rows, errors };
};

const parseDeviceExport = (text: string): ImportParseResult => {
  const [header, ...lines] = linesOf(text);
  const headerMatches =
    header &&
    DEVICE_EXPORT_HEADERS.every(
      (name, index) => (header.cells[index] ?? '').toLowerCase() === name.toLowerCase()
    );
  if (!headerMatches) return { rows: [], errors: [], fileError: 'IMPORT_HEADER_MISMATCH' };

  const rows: ImportRow[] = [];
  const errors: ImportRowError[] = [];
  const fail = (row: number, messageKey: string) => errors.push({ row, messageKey });
  for (const line of lines) {
    if (line.blank) continue;
    const [
      employeeId = '',
      dayText = '',
      startText = '',
      endText = '',
      breakStartText = '',
      breakEndText = '',
    ] = line.cells;
    if (line.cells.length < DEVICE_EXPORT_HEADERS.length) {
      fail(line.row, 'IMPORT_ROW_COLUMNS_MISSING');
      continue;
    }
    if (!employeeId) {
      fail(line.row, 'IMPORT_ROW_EMPLOYEE_REQUIRED');
      continue;
    }
    if (!IMPORT_EMPLOYEE_ID_PATTERN.test(employeeId)) {
      fail(line.row, 'IMPORT_ROW_EMPLOYEE_INVALID');
      continue;
    }
    const day = parseImportDay(dayText);
    if (!day) {
      fail(line.row, 'IMPORT_ROW_DATE_INVALID');
      continue;
    }
    const start = parseImportTime(startText);
    const end = parseImportTime(endText);
    if (!start || !end) {
      fail(line.row, 'IMPORT_ROW_TIME_INVALID');
      continue;
    }
    if (end <= start) {
      fail(line.row, 'IMPORT_ROW_END_BEFORE_START');
      continue;
    }
    if (Boolean(breakStartText) !== Boolean(breakEndText)) {
      fail(line.row, 'IMPORT_ROW_BREAK_INCOMPLETE');
      continue;
    }
    let breakStart: string | undefined;
    let breakEnd: string | undefined;
    if (breakStartText) {
      breakStart = parseImportTime(breakStartText) ?? undefined;
      breakEnd = parseImportTime(breakEndText) ?? undefined;
      if (!breakStart || !breakEnd) {
        fail(line.row, 'IMPORT_ROW_TIME_INVALID');
        continue;
      }
      if (breakEnd <= breakStart || breakStart < start || breakEnd > end) {
        fail(line.row, 'IMPORT_ROW_BREAK_OUTSIDE');
        continue;
      }
    }
    rows.push({
      row: line.row,
      employeeId,
      day,
      start,
      end,
      ...(breakStart ? { breakStart, breakEnd } : {}),
    });
  }
  return { rows, errors };
};

export const parseAttendanceCsv = (format: ImportFormat, text: string): ImportParseResult => {
  const result = format === 'doorLog' ? parseDoorLog(text) : parseDeviceExport(text);
  if (!result.fileError && result.rows.length === 0)
    return { ...result, fileError: 'IMPORT_NO_VALID_ROWS' };
  return result;
};
